import { readingMinutes } from "../lib/readingTime";
import ReadingTime from "../components/ReadingTime";
import React, { useEffect } from "react";
import { usePublicLanguage } from "../context/PublicLanguage";
import { articleLanguage, canonicalArticleUrl, hasIndonesianTranslation, articlePath } from "../lib/articleLanguage";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Link2, ChevronRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { publicApi } from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import CategoryTag, { categoryLabel } from "@/components/CategoryTag";
import TheDish from "@/components/TheDish";
import EditorialInline from "../components/EditorialInline";
import { editorialPresentation } from "../lib/editorialText";
import ArticleBody, { SourceAttribution } from "@/components/ArticleBody";
import StoryCard from "@/components/StoryCard";
import { formatDate, timeAgo } from "@/lib/format";

export function ShareBar({ url }) {
  const { t, language } = usePublicLanguage();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("Link copied"));
    } catch { toast.error(t("Unable to copy link")); }
  };
  return <div lang={language} data-testid="share-controls" className="shrink-0">
    <button type="button" onClick={copy} aria-label={t("Copy Link")} title={t("Copy Link")} data-testid="share-copy"
      className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-[#A7ABB0] hover:text-[#FF8A1F] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF8A1F]">
      <Link2 size={15} aria-hidden="true" />
    </button>
  </div>;
}

export default function ArticlePage({ locale = "en" }) {
  const { language, setLanguage, setArticleRoutes, t } = usePublicLanguage();
  const { slug, category } = useParams();
  const navigate = useNavigate();
  const { data: original, isLoading, isError } = useQuery({
    queryKey: ["article", slug],
    queryFn: () => publicApi.article(slug),
    retry: false,
    initialData: () => {
      try { const data = JSON.parse(document.getElementById("td-article-data")?.textContent || "null"); return data?.slug === slug ? data : undefined; }
      catch { return undefined; }
    },
  });

  const { article: localizedArticle, language: contentLanguage } = articleLanguage(original, locale);
  const article = editorialPresentation(localizedArticle);
  const canonicalUrl = article ? canonicalArticleUrl(article, undefined, contentLanguage) : undefined;
  const translated = hasIndonesianTranslation(original);
  useEffect(() => {
    if (!original || !setArticleRoutes) return;
    setArticleRoutes({ en: articlePath(original), ...(translated ? { id: articlePath(original, "id") } : {}) });
    setLanguage(locale);
    return () => setArticleRoutes(null);
    // URL language wins over a stored preference on translated article routes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [original, translated, locale, setArticleRoutes]);

  const unavailable = (locale === "id" && !translated) || (category && original && category !== original.category);
  useSEO(
    article && !unavailable
      ? {
          title: `${article.seo_title || article.headline} — TechDishes`,
          description: article.meta_description || article.excerpt,
          image: article.hero_image,
          url: canonicalUrl,
          lang: contentLanguage,
          alternates: translated ? [{ lang: "en", url: canonicalArticleUrl(original) }, { lang: "id", url: canonicalArticleUrl(original, undefined, "id") }] : [],
          type: "article",
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: article.headline,
            description: article.meta_description || article.excerpt,
            mainEntityOfPage: canonicalUrl,
            image: [article.hero_image],
            datePublished: article.published_at,
            dateModified: article.updated_at || article.published_at,
            author: [{ "@type": article.author_detail ? "Person" : "Organization", name: article.author_detail?.name || "TechDishes Editorial" }],
            publisher: { "@type": "Organization", name: "TechDishes" },
            articleSection: article.category,
            inLanguage: contentLanguage,
          },
        }
      : { title: "TechDishes", robots: unavailable ? "noindex, follow" : "index, follow" }
  );

  if (isLoading) return <div className="mx-auto max-w-3xl px-5 py-24 text-[#A7ABB0]">{t("Loading article…")}</div>;
  if (isError || !article || unavailable)
    return (
      <div className="mx-auto max-w-3xl px-5 py-24 text-center">
        <p className="text-white text-2xl font-display font-bold mb-3">{locale === "id" && original && !translated ? "Terjemahan bahasa Indonesia belum tersedia." : t("Story not found")}</p>
        {locale === "id" && original && !translated && <Link to={articlePath(original, "en")} className="block text-[#FF8A1F] mb-4">Baca dalam bahasa Inggris</Link>}
        <Link to="/" className="td-eyebrow" style={{ color: "#FF8A1F" }}>
          {t("Back to home")}
        </Link>
      </div>
    );

  const a = article;

  return (
    <article lang={contentLanguage} className="pb-10">
      {/* Breadcrumb */}
      <div className="mx-auto max-w-[1160px] px-5 md:px-8 pt-6">
        <nav lang={language} className="flex items-center gap-2 td-eyebrow" style={{ color: "#71767c", fontSize: "0.58rem" }} aria-label={t("Breadcrumb")}>
          <Link to="/" className="hover:text-white">{t("Home")}</Link>
          <ChevronRight size={12} />
          <Link to={`/${a.category}`} className="hover:text-white">{categoryLabel(a.category, language)}</Link>
        </nav>
      </div>

      {/* Header */}
      <header lang={language} className="mx-auto max-w-[820px] px-5 md:px-8 pt-5 md:pt-6">
        <CategoryTag category={a.category} />
        <h1 lang={contentLanguage} className="td-article-headline font-display font-extrabold tracking-tight text-white mt-3 text-3xl md:text-4xl lg:text-[3.1rem]" style={{ letterSpacing: "-0.03em" }}>
          {a.headline}
        </h1>
        <p lang={contentLanguage} className="mt-3 text-[#A7ABB0] leading-relaxed font-display" style={{ fontSize: "1.3rem" }}>
          {a.subheadline}
        </p>

        <div className="mt-5 flex flex-wrap items-center gap-3 justify-between border-y py-3" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <div className="flex items-center gap-3">
            {a.author_detail && (
              <img src={a.author_detail.avatar} alt={a.author_detail.name} className="w-10 h-10 rounded-full object-cover" style={{ background: "#111315" }} />
            )}
            <div>
              <div className="text-white font-display font-semibold text-sm">{a.author_detail?.name || 'TechDishes Editorial'}</div>
              <div className="td-eyebrow" style={{ color: "#71767c", fontSize: "0.55rem" }}>
                {a.author_detail?.role}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="td-eyebrow" style={{ color: "#71767c", fontSize: "0.56rem" }}>
              <div>{t("Published")} {formatDate(a.published_at, language)}</div>
              <div className="inline-flex items-center gap-1 mt-0.5">
                {readingMinutes(a) && <><ReadingTime article={a} /> <span aria-hidden="true">·</span> </>}{t("Updated")} {timeAgo(a.updated_at, language)}
              </div>
            </div>
            <ShareBar url={canonicalUrl} />
          </div>
        </div>
      </header>

      {/* Hero image */}
      <figure className="mx-auto max-w-[820px] px-5 md:px-8 my-5">
        <div className="article-hero-media" data-testid="article-hero-media">
          <img src={a.hero_image} alt={a.hero_alt || a.headline} width="1600" height="900" className="w-full h-full object-cover" style={{ objectPosition: "center", background: "#111315" }} />
        </div>
        {a.hero_caption && <figcaption className="mt-2.5 text-[#71767c] text-sm font-mono-td px-1">{a.hero_caption}</figcaption>}
      </figure>

      {/* Reading column */}
      <div className="mx-auto max-w-[820px] px-5 md:px-8">
        <div className="min-w-0">
          <TheDish dish={a.the_dish} />
          {a.key_takeaways?.length > 0 && JSON.stringify(a.key_takeaways) !== JSON.stringify(a.the_dish?.key_takeaways) && <section className="my-5"><h2 lang={language} className="text-white font-bold">{t("Key takeaways")}</h2><ul className="list-disc pl-5 text-[#E9E9E4]">{a.key_takeaways.map((item, i) => <li key={i}><EditorialInline text={item} /></li>)}</ul></section>}
          <ArticleBody blocks={a.body} sources={a.sources || []} />

          {/* Tags */}
          {a.tags?.length > 0 && (
            <div className="mt-10 flex flex-wrap gap-2">
              {a.tags.map((t) => (
                <span key={t} className="px-3 py-1.5 rounded-full border text-[#A7ABB0] font-mono-td" style={{ borderColor: "rgba(255,255,255,0.1)", fontSize: "0.72rem" }}>
                  #{t}
                </span>
              ))}
            </div>
          )}

          {/* Sources */}
          <SourceAttribution sources={a.sources || []} />

          {/* Related Dishes */}
          {a.related_detail?.length > 0 && (
            <section lang={language} className="mt-14">
              <div className="flex items-center gap-2.5 mb-6 border-b pb-3" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
                <span className="font-display font-extrabold text-white text-xl tracking-tight">{t("Related Dishes")}</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-8">
                {a.related_detail.map((r) => (
                  <StoryCard key={r.slug} article={r} size="sm" showExcerpt={false} />
                ))}
              </div>
            </section>
          )}

          <div className="mt-12">
            <button lang={language} onClick={() => navigate(-1)} className="inline-flex items-center gap-2 td-eyebrow text-[#A7ABB0] hover:text-white transition-colors">
              <ArrowLeft size={14} /> {t("Back")}
            </button>
          </div>
        </div>


      </div>
    </article>
  );
}
